import { Controller } from "@hotwired/stimulus"
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import PrimeVue from 'primevue/config'
import ConfirmDialog from 'primevue/confirmdialog'
import ConfirmationService from 'primevue/confirmationservice'

export default class extends Controller {
  static values = {
    expiration: Number,
    warning: Number,
    niceWarning: String,
    refreshPath: String,
    translations: Object
  }

  connect() {
    this.t = (key) => {
      return this.translationsValue[key] || key
    }

    this.setup()
    this.startTimer()
  }

  disconnect() {
    this.clearTimers()
    if (this.vueApp) {
      this.vueApp.unmount()
    }
  }

  setup() {
    this.vueApp = createApp({
      components: {
        ConfirmDialog
      },
      template: '<ConfirmDialog :closable="false"></ConfirmDialog>'
    })

    this.vueApp.use(PrimeVue, { unstyled: true })
    this.vueApp.use(ConfirmationService)

    const container = document.createElement('div')
    const modalSizing = document.createElement('div')
    modalSizing.innerHTML = '<div id="modal-box-settings" data-controller="modal-size" data-modal-size-size-value="small"></div>'

    container.style.display = 'none'
    container.appendChild(modalSizing)
    document.body.appendChild(container)

    this.vueInstance = this.vueApp.mount(container)
    this.confirmService = this.vueApp.config.globalProperties.$confirm
  }

  startTimer() {
    const warningTime = this.expirationValue - this.warningValue

    this.clearTimers()
    this.warningTimer = setTimeout(() => {
      this.showTimeoutWarning()
    }, warningTime * 1000)
  }

  showTimeoutWarning() {
    this.confirmService.require({
      message: "%{time}",
      header: this.t('session.timeout_header'),
      icon: 'fa-light fa-triangle-exclamation',
      acceptLabel: this.t('session.continue'),
      rejectLabel: this.t('session.logout'),
      acceptClass: 'button',
      rejectClass: 'button secondary',
      accept: () => {
        this.clearCountdown()
        this.extendSession()
      },
      reject: () => {
        this.clearCountdown()
        this.forceLogout()
      }
    })

    this.startCountdown()
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
      this.coundownTimer = null
    }
  }

  startCountdown() {
    this.remainingTime = this.warningValue
    this.countdownTimer = setInterval(() => {
      this.remainingTime--
      if (this.remainingTime < 0) {
        this.clearCountdown()
        this.forceLogout()
        return
      }
      this.updateDialogMessage()
    }, 1000)
  }

  updateDialogMessage() {
    const countdown = document.querySelector("[data-pc-section='message']")
    if (countdown) {
      countdown.textContent = this.getCountdownMessage()
    }
  }

  getCountdownMessage() {
    var min = Math.floor(this.remainingTime / 60)
    var sec = this.remainingTime % 60
    const minText = min == 1 ? this.t('datetime.distance_in_words.x_minutes.one') : this.t('datetime.distance_in_words.x_minutes.other').replace("%{count}", min)
    const secText = sec == 1 ? this.t('datetime.distance_in_words.x_seconds.one') : this.t('datetime.distance_in_words.x_seconds.other').replace("%{count}", sec)

    var text

    if (min > 0 ) {
      text = `${minText} ${secText}`
    } else {
      text = `${secText}`
    }

    return this.t('session.timeout_warning').replace("%{time}", text)
  }

  forceLogout() {
    fetch('/logout', {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      window.location.href = '/login'
    })
  }

  extendSession() {
    // Make AJAX call to extend session
    fetch(this.refreshPathValue, {
      method: 'GET',
      headers: {
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
      }
    })
    .then(response => {
      if (response.ok) {
        // Session extended, restart the timer
        this.clearTimers()
        this.startTimer()
      }
    })
  }

  clearTimers() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer)
      this.warningTimer = null
    }
    if (this.coutdownTimer) {
      clearTimeout(this.countdownTimer)
      this.countdownTimer = null
    }
  }

  // Reset timer on user activity
  resetTimer() {
    this.startTimer()
  }
}
